import React, { useRef, useState } from 'react';
import { Input } from 'src/components/ui';
import { getTestId } from '../hooks/useTestId';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useDarkMode } from '../context/DarkModeContext';
import classnames from '../util/classnames';
import Icon from 'src/components/Icon';

type InlineComposeProps = {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: React.KeyboardEventHandler;
  placeholder?: string;
  rows?: number;
  file: File | null;
  onFileSelected: (file: File | null) => void;
  accept?: string;
  disabled?: boolean;
  clearable?: boolean;
  /** When provided, shows a hint below the textarea and flags it once `value` exceeds this length. */
  maxLength?: number;
};

function getFileIcon(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'pdf':
      return 'fa fa-file-pdf';
    case 'doc':
    case 'docx':
      return 'fa fa-file-word';
    case 'png':
    case 'jpg':
    case 'jpeg':
      return 'FileImage';
    default:
      return 'File';
  }
}

export default function InlineCompose({
  value,
  onChange,
  onKeyDown,
  placeholder,
  rows = 3,
  file,
  onFileSelected,
  accept,
  disabled,
  clearable = true,
  maxLength,
}: InlineComposeProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [attachHovered, setAttachHovered] = useState(false);
  const { isDarkMode } = useDarkMode();

  const isOverLimit = maxLength != null && value.length > maxLength;

  const canHover = !file && !disabled;

  function handlePanelClick() {
    if (!file && !disabled) {
      fileInputRef.current?.click();
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0] ?? null;
    onFileSelected(selected);
    setFileInputKey((k) => k + 1);
  }

  function handleRemoveFile(e: React.MouseEvent) {
    e.stopPropagation();
    onFileSelected(null);
  }

  return (
    <>
      <div className="flex items-stretch" style={{ gap: '8px' }}>
        {/* Textarea */}
        <div style={{ flex: '1 1 80%' }}>
          <Input
            type="textarea"
            value={value}
            onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
            style={{ height: '100%', resize: 'none' }}
          />
        </div>

        {/* Attachment panel */}
        <div
          className="flex flex-col items-center justify-center border rounded"
          style={{
            position: 'relative',
            flex: '0 0 auto',
            width: '20%',
            minWidth: '80px',
            maxWidth: '100px',
            cursor: canHover ? 'pointer' : 'default',
            padding: '8px 4px',
            overflow: 'hidden',
            background: isDarkMode
              ? canHover && attachHovered ? '#32363d' : '#2a2d33'
              : canHover && attachHovered ? '#e9ecef' : '#f8f9fa',
            borderColor: canHover && attachHovered ? (isDarkMode ? '#40444f' : '#6c757d') : undefined,
            transition: 'background 0.15s, border-color 0.15s',
          }}
          onClick={handlePanelClick}
          onMouseEnter={() => canHover && setAttachHovered(true)}
          onMouseLeave={() => setAttachHovered(false)}
        >
          {file ? (
            <>
              {clearable && (
                <button
                  type="button"
                  className="btn btn-link text-danger p-0"
                  style={{ position: 'absolute', top: 4, right: 4, lineHeight: 1, background: 'transparent' }}
                  onClick={handleRemoveFile}
                  disabled={disabled}
                  title={t`Remove file`}
                  {...getTestId('inline-compose', '.', 'remove-file')}
                >
                  <Icon icon="X" weight="regular" />
                </button>
              )}
              <Icon icon={getFileIcon(file.name)} fontSize="1.4rem" style={{ marginBottom: '4px' }} />
              <div
                style={{
                  fontSize: '0.7rem',
                  wordBreak: 'break-all',
                  textAlign: 'center',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  padding: '0 4px',
                }}
                title={file.name}
              >
                {file.name}
              </div>
            </>
          ) : (
            <>
              <Icon icon="Paperclip" style={{ fontSize: '1.4rem', marginBottom: '4px' }} />
              <small style={{ fontSize: '0.7rem' }}>
                <Trans>Attach</Trans>
              </small>
            </>
          )}
        </div>

        {/* Hidden file input */}
        <input
          key={fileInputKey}
          ref={fileInputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          disabled={disabled}
        />
      </div>

      {maxLength != null && (
        <div className={classnames('small mt-1', isOverLimit ? 'text-danger' : 'text-muted')} {...getTestId('inline-compose', '.', 'max-length-hint')}>
          <Trans>
            Maximum {maxLength.toLocaleString()} characters. If your message is longer, send it as an attachment instead.
          </Trans>
        </div>
      )}
    </>
  );
}
